/**
 * SOL-04 — shared JHA sign-status truth.
 *
 * The row-level JHA.signedAt is a server creation timestamp (non-nullable in
 * schema.prisma), so it can NEVER mean "fully signed". Real sign-off truth
 * lives per crew member (crewMembers[].signature). Extracted verbatim from
 * ProjectPacketView.tsx (SOL-003 / LUNA-002 / DELTA-02) so JHADetailPage and
 * the My Jobs packet share ONE truth function instead of duplicating logic.
 */

/**
 * needsForemanReview (worker-created emergency JHAs) is the most important
 * status and wins over everything.
 */
/**
 * Generic over the input so full JHA rows (extra fields ignored) type-check.
 */
/**
 * Generic over the input so full JHA rows (extra fields ignored) type-check:
 * extra properties land in T, so no excess-property error, and typed JHA
 * interfaces satisfy the structural constraint.
 */
export function jhaSignStatus<T extends object>(jha: T & { crewMembers?: unknown; needsForemanReview?: boolean }): string {
  if (jha.needsForemanReview) return 'awaiting foreman review';
  const raw = jha.crewMembers;
  if (Array.isArray(raw)) {
    const members = raw.filter((m): m is Record<string, any> => m != null && typeof m === 'object');
    // Mixed or fully-legacy rosters: per-member signature truth is unavailable.
    if (members.length !== raw.length) return 'signature status unknown — open the JHA to verify';
    if (members.length > 0) {
      const signed = members.filter((m) => typeof m.signature === 'string' && m.signature.trim() !== '').length;
      if (signed === members.length) return 'signed';
      if (signed > 0) return `${signed} of ${members.length} crew signed`;
      return 'open — sign it';
    }
  }
  // No roster at all (or an empty one): nothing supports any signature claim.
  return 'signature status unknown — open the JHA to verify';
}
